import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader, RarityBadge } from "@/components/Atoms";
import { Search, Wrench, Hammer, Beaker, ImageOff } from "lucide-react";
import { ChilletSkull } from "@/components/ChilletSkull";
import { PalPortrait, ElementChip } from "@/components/PalAssets";

// Match the backend's slugify rules in /app/backend/api/routes/paldex.py
const slugify = (name) => {
  if (!name) return "";
  return String(name)
    .replace(/[\u2018\u2019']/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
};

const TABS = [
  { id: "paldex", label: "Paldex", icon: ChilletSkull },
  { id: "items", label: "Items", icon: Hammer },
  { id: "structures", label: "Structures", icon: Wrench },
  { id: "tech", label: "Tech", icon: Beaker },
];

export default function Lookup() {
  const [tab, setTab] = useState("paldex");
  return (
    <div>
      <PageHeader title="Wiki & Paldex" subtitle="Browse Pals, items, structures, and tech." />
      <div className="flex gap-1 mb-6 border-b border-white/5 overflow-x-auto" data-testid="lookup-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`lookup-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id ? "border-[var(--teal)] text-[var(--teal)]" : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "paldex" && <Paldex />}
      {tab === "items" && <CatalogBrowser endpoint="/lookup/item" placeholder="Search items… (e.g., pal sphere)" testIdPrefix="items" />}
      {tab === "structures" && <CatalogBrowser endpoint="/lookup/structure" placeholder="Search structures… (e.g., furnace)" testIdPrefix="structures" />}
      {tab === "tech" && <CatalogBrowser endpoint="/lookup/tech" placeholder="Search tech… (e.g., handgun)" testIdPrefix="tech" />}
    </div>
  );
}

const ELEMENTS = ["", "Neutral", "Fire", "Water", "Grass", "Electric", "Ice", "Ground", "Dark", "Dragon"];

function rarityTier(r) {
  if (!r && r !== 0) return "common";
  if (r >= 15) return "legendary";
  if (r >= 10) return "epic";
  if (r >= 7) return "rare";
  if (r >= 4) return "uncommon";
  return "common";
}

function Paldex() {
  const [name, setName] = useState("");
  const [element, setElement] = useState("");
  const [page, setPage] = useState(1);
  // Default-off: alphas just inflate the dex from a roster-completeness
  // standpoint. Completionists can flip them on with one click.
  const [includeAlpha, setIncludeAlpha] = useState(false);
  const [data, setData] = useState({ results: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), limit: "24" });
    if (name) params.set("name", name);
    if (element) params.set("element", element);
    if (includeAlpha) params.set("include_alpha", "true");
    api.get(`/lookup/paldex?${params}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [name, element, page, includeAlpha]);

  return (
    <>
      <div className="grid sm:grid-cols-[1fr_220px_auto] gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input className="input pl-9" placeholder="Search Pal name…" value={name} onChange={(e) => { setName(e.target.value); setPage(1); }} data-testid="paldex-search" />
        </div>
        <select className="input" value={element} onChange={(e) => { setElement(e.target.value); setPage(1); }} data-testid="paldex-element-filter">
          {ELEMENTS.map((el) => <option key={el} value={el}>{el || "All elements"}</option>)}
        </select>
        <label
          className="flex items-center gap-2 px-3 h-10 rounded border border-white/10 bg-black/30 text-xs cursor-pointer select-none hover:border-[var(--teal)]/40 transition-colors"
          data-testid="paldex-alpha-toggle"
        >
          <input
            type="checkbox"
            className="accent-[var(--teal)]"
            checked={includeAlpha}
            onChange={(e) => { setIncludeAlpha(e.target.checked); setPage(1); }}
          />
          <span>Show Alpha variants</span>
        </label>
      </div>

      {loading ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 fade-up">
            {data.results.map((p) => {
              const tier = rarityTier(p.Rarity);
              return (
                <Link
                  key={p.Name}
                  to={`/paldex/${slugify(p.Name)}`}
                  className={`glass-card p-3 text-left rarity-${tier} hover:border-[var(--teal)] transition-colors`}
                  data-testid={`paldex-card-${p.Name}`}
                >
                  <PalPortrait name={p.Name} wikiImage={p.WikiImage} size="w-full aspect-square" className="mb-2" />
                  <div className="text-sm truncate">{p.Name}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(p.Elements || []).slice(0, 2).map((e) => (
                      <ElementChip key={e} element={e} size={11} showLabel={false} />
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>

          {data.results.length === 0 && <p className="text-center text-[var(--muted)] py-12">No Pals match.</p>}

          <div className="flex items-center justify-center gap-3 mt-6">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-secondary btn-sm" data-testid="paldex-prev-page">Prev</button>
            <span className="text-sm text-[var(--muted)]">Page {page} · {data.total} total</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * 24 >= data.total} className="btn btn-secondary btn-sm" data-testid="paldex-next-page">Next</button>
          </div>
        </>
      )}
    </>
  );
}

const PAGE_SIZE = 60;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function iconHref(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function CatalogBrowser({ endpoint, placeholder, testIdPrefix }) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], total: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const toast = useToast();

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Load categories once per endpoint
  useEffect(() => {
    setCategory("");
    setPage(1);
    api.get(`${endpoint}/categories`).then(({ data }) => setCategories(data.categories || [])).catch(() => setCategories([]));
  }, [endpoint]);

  // Fetch list
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
    if (debouncedQ) params.set("name", debouncedQ);
    if (category) params.set("category", category);
    api.get(`${endpoint}?${params}`)
      .then(({ data }) => setData(data))
      .catch((e) => { setData({ results: [], total: 0 }); toast.error(apiError(e, "Failed to load")); })
      .finally(() => setLoading(false));
  }, [endpoint, debouncedQ, category, page]);

  // reset to page 1 on filter changes
  useEffect(() => { setPage(1); }, [debouncedQ, category]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);

  return (
    <div>
      <div className="grid sm:grid-cols-[1fr_220px] gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            className="input pl-9"
            placeholder={placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid={`${testIdPrefix}-search-input`}
          />
        </div>
        <select
          className="input"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid={`${testIdPrefix}-category-filter`}
        >
          <option value="">All categories ({data.total || 0})</option>
          {categories.map(([name, count]) => (
            <option key={name} value={name}>{name} ({count})</option>
          ))}
        </select>
      </div>

      {loading ? <Loader /> : (
        <>
          {data.results.length === 0 ? (
            <p className="text-center text-[var(--muted)] py-12" data-testid={`${testIdPrefix}-empty`}>
              {debouncedQ ? `Nothing matches "${debouncedQ}".` : "No entries available."}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 fade-up">
              {data.results.map((r, idx) => (
                <CatalogCard
                  key={`${r.id || r.asset || r.name}-${idx}`}
                  row={r}
                  onClick={() => setActive(r)}
                  testId={`${testIdPrefix}-card-${idx}`}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary btn-sm"
                data-testid={`${testIdPrefix}-prev-page`}
              >Prev</button>
              <span className="text-sm text-[var(--muted)]">
                Page {page} / {totalPages} · {data.total} total
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn btn-secondary btn-sm"
                data-testid={`${testIdPrefix}-next-page`}
              >Next</button>
            </div>
          )}
        </>
      )}

      {active && <CatalogDetail row={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function CatalogIcon({ src, alt, size = "w-full aspect-square" }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div className={`${size} bg-black/40 rounded flex items-center justify-center text-[var(--muted)]`}>
        <ImageOff size={20} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      className={`${size} object-contain bg-black/30 rounded p-1.5`}
      loading="lazy"
    />
  );
}

function CatalogCard({ row, onClick, testId }) {
  const href = iconHref(row.icon_url);
  return (
    <button onClick={onClick} className="glass-card p-3 text-left hover:border-[var(--teal)] transition-colors" data-testid={testId}>
      <CatalogIcon src={href} alt={row.name} />
      <div className="text-sm truncate mt-2">{row.name}</div>
      <div className="flex items-center justify-between mt-1">
        {row.category && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] truncate">{row.category}</span>
        )}
        {typeof row.level === "number" && row.level > 0 && (
          <span className="text-[10px] text-[var(--teal)] glow-teal shrink-0">Lv {row.level}</span>
        )}
      </div>
    </button>
  );
}

function CatalogDetail({ row, onClose }) {
  const href = iconHref(row.icon_url);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-md" onClick={onClose}>
      <div className="glass-card max-w-xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="catalog-detail-modal">
        <div className="flex items-start gap-4">
          <CatalogIcon src={href} alt={row.name} size="w-24 h-24" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl mb-1 truncate" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{row.name}</h2>
            <div className="flex flex-wrap gap-2 text-xs">
              {row.category && <span className="badge text-[var(--muted)] border-white/10">{row.category}</span>}
              {typeof row.level === "number" && row.level > 0 && (
                <span className="badge text-[var(--teal)] border-[var(--teal)]/40">Level {row.level}</span>
              )}
              {row.id && <span className="badge text-[var(--muted)] border-white/10 truncate">ID: {row.id}</span>}
              {row.asset && <span className="badge text-[var(--muted)] border-white/10 truncate">Asset: {row.asset}</span>}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-sm btn-secondary" data-testid="close-catalog-detail">✕</button>
        </div>

        {row.description && (
          <p className="text-sm text-[var(--muted)] mt-4 italic">{row.description}</p>
        )}

        {typeof row.build_work === "number" && row.build_work > 0 && (
          <div className="mt-4 text-sm">
            <span className="text-[var(--muted)]">Build work: </span>
            <span className="glow-teal">{row.build_work.toLocaleString()}</span>
          </div>
        )}

        {Array.isArray(row.materials) && row.materials.length > 0 && (
          <div className="mt-4">
            <h4 className="label mb-2">Materials</h4>
            <ul className="text-sm space-y-1">
              {row.materials.map((m, i) => (
                <li key={`${m.item}-${i}`} className="flex justify-between bg-black/20 px-3 py-1.5 rounded">
                  <span>{m.item}</span>
                  <span className="text-[var(--muted)]">×{m.qty}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
